import { useEffect } from 'react';

import {
  useTradeTapeIdsList,
  useTradeTapeService,
} from '@paradigm/features/src/unified-rfqs/contexts/unified-trade-tapes';

import { MAX_TRADES } from '#/features/terminal/block-trade-context';
import { useTapeSnapshotStore } from '#/features/terminal/tape-snapshot-store';

/**
 * Mirrors the Trade Tape tab's visible rows into the tape-snapshot store on
 * every tape update, so `/analyze` can attach a "full tape" context even when
 * this tab is no longer mounted (the tape tab and the terminal tab are
 * mutually exclusive in bottom-tab pin mode). Renders nothing.
 *
 * Must live inside the tab's `UnifiedRfqTradeTapeProvider` — that instance is
 * the one whose fetcher actually loads the tape; the providers wrapped
 * around the terminal itself never fetch.
 */
export default function TapeSnapshotMirror() {
  const tradeIds = useTradeTapeIdsList();
  const service = useTradeTapeService();
  const setSnapshot = useTapeSnapshotStore((s) => s.setSnapshot);

  useEffect(() => {
    const trades = tradeIds
      .slice(0, MAX_TRADES)
      .map((id) => service.selectById(id))
      .filter((t): t is NonNullable<typeof t> => t != null);
    // Never clobber a useful snapshot with an empty one: remounts (tab
    // switches, filter changes) briefly render zero rows before data lands,
    // and for agent context a stale tape beats an empty tape.
    if (trades.length === 0) return;
    setSnapshot(trades, tradeIds.length);
  }, [tradeIds, service, setSnapshot]);

  return null;
}
